import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { formatCurrency } from "@utils/formatNumber";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useCommonStyles } from "../hooks/useCommonStyles";
import { useGetTotalBreakDownValues } from "../hooks/useGetTotalBreakDownValues";
import { setTotalBreakdownValues } from "../utils/setTotalBreakDownValues";

interface ITotalBreakdownProps {
  translate: (text: any, options?: any) => string;
}

export const TotalBreakdown = ({ translate }: ITotalBreakdownProps) => {
  const { watch, setValue } = useFormContext();
  const { inputContainer } = useCommonStyles();
  const { sumOfWithHoldingTax, total, totalDiscount, totalTax, subtotal } =
    useGetTotalBreakDownValues(watch);
  const { palette } = useTheme();
  const currency = watch("currency");
  const discountType: string = watch("discountType") ?? "amount";
  const totalDiscountRaw = watch("totalDiscount");
  
  const items = (watch("items") ?? []) as Array<{
    vatPercentage?: number;
    unitPrice?: number;
    quantity?: number;
  }>;
  
  const grossSubtotal = items.reduce(
    (sum, i) => sum + (Number(i?.unitPrice) || 0) * (Number(i?.quantity) || 0),
    0,
  );
  const taxesArray = (watch("taxes") ?? []) as Array<{
    taxType?: string;
    vatPercentage?: number;
    isWithHoldingTax?: boolean;
  }>;

  const collectRates = (rates: Array<number | undefined>) => {
    const unique = Array.from(
      new Set(
        rates
          .filter((r): r is number => typeof r === "number" && r > 0)
          .map((r) => Number(r)),
      ),
    );
    return unique;
  };

  const taxRates = collectRates([
    ...items.map((i) => i?.vatPercentage),
    ...taxesArray
      .filter(
        (t) =>
          (t.isWithHoldingTax === false ||
            t.taxType === "VAT" ||
            t.taxType === "IGIC") &&
          t.isWithHoldingTax !== true,
      )
      .map((t) => t?.vatPercentage),
  ]);

  const whtRates = collectRates(
    taxesArray
      .filter(
        (t) =>
          t.isWithHoldingTax === true ||
          t.taxType === "WHT" ||
          t.taxType === "IRPF",
      )
      .map((t) => t?.vatPercentage),
  );

  const formatRateLabel = (rates: number[]) =>
    rates.length === 1 ? ` (${rates[0]}%)` : "";

  // Sync computed totals back to RHF so the schema (which requires
  // totalWithoutTax) validates and the API receives the same numbers
  // we display in the breakdown card.
  useEffect(() => {
    setTotalBreakdownValues({
      setValue,
      subtotal: grossSubtotal,
      totalTax,
      total,
    });
  }, [grossSubtotal, totalTax, total, setValue]);

  const Row = ({
    label,
    value,
    isBold = false,
  }: {
    label: string;
    value: string | number;
    isBold?: boolean;
  }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 1 }}>
      <Typography
        variant="body2"
        sx={{
          fontSize: label === "Total amount" ? "20px" : "16px",
          color: palette.text.label,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        color={palette.text.primary}
        sx={{
          fontWeight: isBold ? 700 : 400,
          fontSize: label === "Total amount" ? "28px" : "20px",
        }}
      >
        {`${currency ? currency : ""} ${formatCurrency(
          Number(value),
          "0,0.00",
        )}`}
      </Typography>
    </Box>
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h6">{translate("total_breakdown")}</Typography>
      </Grid>

      <Grid item xs={12}>
        <Box sx={inputContainer}>
          <Stack spacing={0}>
            <Row label={translate("subtotal")} value={grossSubtotal} />
            <Row
              label={`${translate("tax")}${formatRateLabel(taxRates)}`}
              value={totalTax.toFixed(2)}
            />
            <Row
              label={
                discountType === "percentage" && totalDiscountRaw
                  ? `${translate("discount")} (${totalDiscountRaw}%)`
                  : translate("discount")
              }
              value={totalDiscount}
            />
            {sumOfWithHoldingTax > 0 && (
              <Row
                label={`${translate("wht_tax")}${formatRateLabel(whtRates)}`}
                value={sumOfWithHoldingTax}
              />
            )}
            <Divider sx={{ my: 1 }} />

            <Row
              label={translate("total_amount")}
              value={total.toFixed(2)}
              isBold
            />
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
};
